import type { ChangeEvent, DragEvent, FC, ReactNode } from 'react'
import type { PaymentMethod, Plan, PublicPaymentSettings } from '@/types'

import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { cn } from '@/lib/utils'
import { formatInr } from '@/lib/format'
import { useToast } from '@/components/generals/toast'
import { logoMarkDataUri } from '@/components/generals/logo'
import { Field, TextInput } from '@/components/generals/form'

import {
    Check,
    Copy,
    FileText,
    ImageUp,
    Landmark,
    LoaderCircle,
    QrCode,
    Receipt,
    Smartphone,
    X
} from 'lucide-react'

import { PAYMENT_METHODS } from '@/constants/campus'

export interface PaymentErrors {
    reference?: string
    proof?: string
    confirmed?: string
}

interface PaymentPanelProps {
    idPrefix: string
    plan: Plan
    settings: PublicPaymentSettings | null
    payerNote: string
    method: PaymentMethod
    onMethodChange: (method: PaymentMethod) => void
    reference: string
    onReferenceChange: (value: string) => void
    proof: File | null
    onProofChange: (file: File | null) => void
    confirmed: boolean
    onConfirmedChange: (value: boolean) => void
    errors: PaymentErrors
}

const METHOD_ICONS: Record<PaymentMethod, typeof QrCode> = {
    UPI: QrCode,
    'Bank Transfer': Landmark,
    'Campus Cash Receipt': Receipt
}

const MAX_PROOF_BYTES = 8 * 1024 * 1024
const PROOF_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']

// Accepts a payment screenshot only if the server will accept it too.
export const validateProof = (file: File): string | null => {
    if (!PROOF_TYPES.includes(file.type)) return 'Upload a JPG, PNG, WebP or PDF file.'
    if (file.size > MAX_PROOF_BYTES) return 'The screenshot must be 8 MB or smaller.'

    return null
}

export const buildUpiLink = (settings: PublicPaymentSettings, amount: number, note: string): string =>
    `upi://pay?pa=${encodeURIComponent(settings.upiId)}&pn=${encodeURIComponent(settings.payeeName)}&am=${amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(note.slice(0, 40))}`

const settingsTag = (settings: PublicPaymentSettings, column: string): string | undefined =>
    settings.id ? `payment_settings:${settings.id}:${column}` : undefined

export const PaymentPanel: FC<PaymentPanelProps> = ({
    idPrefix,
    plan,
    settings,
    payerNote,
    method,
    onMethodChange,
    reference,
    onReferenceChange,
    proof,
    onProofChange,
    confirmed,
    onConfirmedChange,
    errors
}): ReactNode => {
    const { toast } = useToast()
    const [copied, setCopied] = useState(false)
    const [dragging, setDragging] = useState(false)
    const [preview, setPreview] = useState<string | null>(null)
    const [localProofError, setLocalProofError] = useState<string | null>(null)

    const activeMethod = PAYMENT_METHODS.find(item => item.value === method) ?? PAYMENT_METHODS[0]
    const proofError = localProofError ?? errors.proof

    useEffect(() => {
        if (!proof || !proof.type.startsWith('image/')) {
            setPreview(null)
            return
        }

        const url = URL.createObjectURL(proof)

        setPreview(url)

        return () => URL.revokeObjectURL(url)
    }, [proof])

    const pickFile = (file: File | null | undefined) => {
        if (!file) return

        const problem = validateProof(file)

        setLocalProofError(problem)

        if (!problem) onProofChange(file)
    }

    const onInput = (event: ChangeEvent<HTMLInputElement>) => {
        pickFile(event.target.files?.[0])
        event.target.value = ''
    }

    const onDrop = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault()
        setDragging(false)
        pickFile(event.dataTransfer.files?.[0])
    }

    const copyUpi = async () => {
        if (!settings) return

        try {
            await navigator.clipboard.writeText(settings.upiId)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1800)
        }
        catch {
            toast('Copy is blocked in this window', { description: `Select the UPI ID ${settings.upiId} and copy it manually.`, tone: 'info' })
        }
    }

    const note = `GKV ${plan.label} pass ${payerNote}`.trim()

    return (
        <div className='space-y-5'>
            <fieldset>
                <legend className='mb-2.5 text-[13px] font-semibold text-slate-800'>
                    Payment method
                </legend>

                <div className='grid gap-2.5 sm:grid-cols-3'>
                    {PAYMENT_METHODS.map(item => {
                        const Icon = METHOD_ICONS[item.value]
                        const selected = item.value === method

                        return (
                            <label
                                key={item.value}
                                className={cn(
                                    'relative flex cursor-pointer items-start gap-3 rounded-2xl border p-3.5 transition',
                                    selected ? 'border-indigo-500 bg-indigo-50/60 ring-4 ring-indigo-500/10' : 'border-slate-200 bg-white hover:border-slate-300'
                                )}
                            >
                                <input
                                    type='radio'
                                    name={`${idPrefix}-method`}
                                    value={item.value}
                                    checked={selected}
                                    onChange={() => onMethodChange(item.value)}
                                    className='sr-only'
                                />

                                <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl', selected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600')}>
                                    <Icon className='size-4' />
                                </span>

                                <span className='min-w-0'>
                                    <span className='block text-sm font-semibold text-ink'>{item.label}</span>
                                    <span className='mt-0.5 block text-xs leading-snug text-slate-500'>{item.hint}</span>
                                </span>

                                {selected && <Check className='absolute right-3 top-3 size-4 text-indigo-600' strokeWidth={3} />}
                            </label>
                        )
                    })}
                </div>
            </fieldset>

            <div className='rounded-2xl border border-slate-200 bg-alabaster p-4 sm:p-5'>
                {!settings ? (
                    <div className='flex items-center gap-2 py-6 text-sm text-slate-500'>
                        <LoaderCircle className='size-4 animate-spin' />
                        Loading the official payment details
                    </div>
                ) : method === 'UPI' ? (
                    <div className='flex flex-col gap-5 sm:flex-row sm:items-center'>
                        <div className='mx-auto shrink-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:mx-0'>
                            <QRCodeSVG
                                value={buildUpiLink(settings, plan.price, note)}
                                size={156}
                                level='H'
                                marginSize={0}
                                bgColor='#FFFFFF'
                                fgColor='#0B1628'
                                title={`UPI QR code to pay ${formatInr(plan.price)}`}
                                imageSettings={{ src: logoMarkDataUri, height: 32, width: 32, excavate: true }}
                            />
                        </div>

                        <div className='min-w-0 flex-1'>
                            <p className='text-xs font-semibold uppercase tracking-[0.14em] text-slate-500'>
                                Scan & pay exactly
                            </p>

                            <p className='mt-1 text-3xl font-extrabold tracking-tight text-ink'>
                                {formatInr(plan.price)}
                            </p>

                            <p className='mt-1 text-sm text-slate-600'>
                                to <span data-mdlfy-cms={settingsTag(settings, 'payee_name')} className='font-semibold text-ink'>{settings.payeeName}</span>
                            </p>

                            <div className='mt-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-white py-1.5 pl-3 pr-1.5'>
                                <span className='text-xs font-semibold text-slate-500'>UPI ID</span>

                                <span data-mdlfy-cms={settingsTag(settings, 'upi_id')} className='min-w-0 flex-1 truncate font-mono text-sm font-bold text-ink'>{settings.upiId}</span>

                                <button
                                    type='button'
                                    onClick={copyUpi}
                                    className='inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-700'
                                >
                                    {copied ? <Check className='size-3.5' /> : <Copy className='size-3.5' />}
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>

                            <a
                                href={buildUpiLink(settings, plan.price, note)}
                                className='mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 md:hidden'
                            >
                                <Smartphone className='size-4' />
                                Pay with a UPI app
                            </a>

                            <p className='mt-3 text-xs leading-relaxed text-slate-500'>
                                Works with GPay, PhonePe, Paytm and BHIM. The amount is filled in for you.
                            </p>
                        </div>
                    </div>
                ) : method === 'Bank Transfer' ? (
                    <div>
                        <p className='text-sm font-semibold text-ink'>
                            Transfer {formatInr(plan.price)} by NEFT / IMPS
                        </p>

                        {settings.bankDetails ? (
                            <p data-mdlfy-cms={settingsTag(settings, 'bank_details')} className='mt-2 whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-3 font-mono text-[13px] leading-relaxed text-slate-700'>{settings.bankDetails}</p>
                        ) : (
                            <p className='mt-2 text-sm leading-relaxed text-slate-600'>
                                Bank account details are shared by the T&P Cell on request. If you do not have them yet, pay by UPI instead or visit the T&P Cell.
                            </p>
                        )}
                    </div>
                ) : (
                    <div>
                        <p className='text-sm font-semibold text-ink'>
                            Pay {formatInr(plan.price)} in cash at the T&P Cell
                        </p>

                        <p className='mt-2 text-sm leading-relaxed text-slate-600'>
                            Collect the official campus receipt and enter its receipt number below. Keep the slip until your pass is verified.
                        </p>
                    </div>
                )}

                {settings?.instructions && (
                    <p data-mdlfy-cms={settingsTag(settings, 'instructions')} className='mt-4 whitespace-pre-wrap border-t border-slate-200 pt-4 text-xs leading-relaxed text-slate-600'>{settings.instructions}</p>
                )}
            </div>

            <Field label={activeMethod.referenceLabel} htmlFor={`${idPrefix}-reference`} required error={errors.reference} hint={method === 'UPI' ? 'Find the 12-digit UTR under transaction details in your UPI app.' : undefined}>
                <TextInput
                    id={`${idPrefix}-reference`}
                    value={reference}
                    onChange={event => onReferenceChange(event.target.value)}
                    placeholder={activeMethod.referencePlaceholder}
                    autoComplete='off'
                    inputMode={method === 'UPI' ? 'numeric' : 'text'}
                    maxLength={40}
                    invalid={Boolean(errors.reference)}
                    describedBy={errors.reference ? `${idPrefix}-reference-error` : `${idPrefix}-reference-hint`}
                    className='font-mono tracking-wide'
                />
            </Field>

            <div>
                <p className='mb-1.5 text-[13px] font-semibold text-slate-800'>
                    Payment screenshot <span className='font-normal text-slate-500'>(optional)</span>
                </p>

                {proof ? (
                    <div className='flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3'>
                        {preview ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={preview} alt='Payment screenshot preview' className='size-14 rounded-xl object-cover ring-1 ring-slate-200' />
                        ) : (
                            <span className='grid size-14 place-items-center rounded-xl bg-rose-50 text-rose-600'>
                                <FileText className='size-6' />
                            </span>
                        )}

                        <div className='min-w-0 flex-1'>
                            <p className='truncate text-sm font-semibold text-ink'>{proof.name}</p>
                            <p className='text-xs text-slate-500'>{(proof.size / 1024 / 1024).toFixed(2)} MB · ready to upload</p>
                        </div>

                        <button
                            type='button'
                            onClick={() => onProofChange(null)}
                            aria-label='Remove screenshot'
                            className='grid size-9 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-ink'
                        >
                            <X className='size-4' />
                        </button>
                    </div>
                ) : (
                    <label
                        htmlFor={`${idPrefix}-proof`}
                        onDragOver={event => {
                            event.preventDefault()
                            setDragging(true)
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        className={cn(
                            'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition',
                            dragging ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        )}
                    >
                        <ImageUp className='size-6 text-indigo-500' />

                        <span className='text-sm font-semibold text-ink'>
                            Drop your screenshot here or <span className='text-indigo-600 underline underline-offset-4'>browse</span>
                        </span>

                        <span className='text-xs text-slate-500'>JPG, PNG, WebP or PDF · up to 8 MB</span>
                    </label>
                )}

                <input id={`${idPrefix}-proof`} type='file' accept='image/jpeg,image/png,image/webp,application/pdf' onChange={onInput} className='sr-only' />

                {proofError && <p className='mt-1.5 text-xs font-medium text-rose-600'>{proofError}</p>}
            </div>

            <div>
                <label className='flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3.5'>
                    <input
                        type='checkbox'
                        checked={confirmed}
                        onChange={event => onConfirmedChange(event.target.checked)}
                        className='mt-0.5 size-4 shrink-0 rounded border-slate-300 accent-indigo-600'
                    />

                    <span className='text-sm leading-relaxed text-slate-700'>
                        I have paid {formatInr(plan.price)} to the official account above, and this reference is from my own payment.
                    </span>
                </label>

                {errors.confirmed && <p className='mt-1.5 text-xs font-medium text-rose-600'>{errors.confirmed}</p>}
            </div>
        </div>
    )
}
